// Web Audio API Synthesizer & Therapeutic Sound Engine for AbilityOS
// Includes Indian Classical Raga Therapy, Solfeggio 432Hz & Binaural Brainwave Entrainment

class WebAudioEngine {
  private ctx: AudioContext | null = null;
  private metronomeTimer: number | null = null;
  private ambientNodes: { oscs: OscillatorNode[]; gain: GainNode } | null = null;
  private isAmbientPlaying = false;

  private initCtx() {
    if (!this.ctx) {
      const AudioCtxClass = window.AudioContext || (window as any).webkitAudioContext;
      if (AudioCtxClass) {
        this.ctx = new AudioCtxClass();
      }
    }
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume();
    }
    return this.ctx;
  }

  /**
   * Play a single musical note (e.g. C4, E4, G4) with a smooth piano/marimba decay envelope
   */
  public playNote(freq: number, waveType: OscillatorType = 'sine', duration = 0.8) {
    const ctx = this.initCtx();
    if (!ctx) return;

    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = waveType;
      osc.frequency.setValueAtTime(freq, ctx.currentTime);

      // Envelope: Fast attack, exponential decay
      gain.gain.setValueAtTime(0.001, ctx.currentTime);
      gain.gain.linearRampToValueAtTime(0.3, ctx.currentTime + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + duration);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + duration);
    } catch (e) {
      console.warn('AudioContext error:', e);
    }
  }

  /**
   * Play an Indian Classical Raga scale note sequence for therapeutic grounding
   * Raga Bhupali (Pentatonic: Sa Re Ga Pa Dha Sa') -> [240, 270, 300, 360, 400, 480]
   * Raga Yaman (Peaceful: Sa Re Ga Ma' Pa Dha Ni Sa') -> [240, 270, 300, 337.5, 360, 400, 450, 480]
   * Raga Bairagi (Serenity: Sa komal-Re Ma Pa komal-Ni Sa') -> [240, 253, 320, 360, 426, 480]
   */
  public playRagaSequence(ragaName: 'bhupali' | 'yaman' | 'bairagi' = 'bhupali') {
    const ragaFrequencies: Record<string, number[]> = {
      bhupali: [240, 270, 300, 360, 400, 480],
      yaman: [240, 270, 300, 337.5, 360, 400, 450, 480],
      bairagi: [240, 253, 320, 360, 426, 480]
    };

    const scale = ragaFrequencies[ragaName] || ragaFrequencies.bhupali;
    scale.forEach((freq, idx) => {
      setTimeout(() => {
        this.playNote(freq, 'sine', 1.2);
      }, idx * 450);
    });
  }

  /**
   * Play a single crisp metronome woodblock tick
   */
  public playMetronomeTick(isAccent = false) {
    const ctx = this.initCtx();
    if (!ctx) return;

    try {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'triangle';
      osc.frequency.setValueAtTime(isAccent ? 1200 : 800, ctx.currentTime);

      gain.gain.setValueAtTime(0.4, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.05);

      osc.connect(gain);
      gain.connect(ctx.destination);

      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.05);
    } catch (e) {
      console.warn('Metronome tick error:', e);
    }
  }

  /**
   * Start Rhythmic Auditory Stimulation (RAS) Metronome Loop
   */
  public startMetronome(bpm: number, onBeat?: (beatCount: number) => void) {
    this.stopMetronome();
    let beatCount = 0;
    const intervalMs = (60 / bpm) * 1000;

    // Play initial beat
    this.playMetronomeTick(true);
    beatCount++;
    if (onBeat) onBeat(beatCount);

    this.metronomeTimer = window.setInterval(() => {
      beatCount++;
      const isAccent = beatCount % 4 === 1;
      this.playMetronomeTick(isAccent);
      if (onBeat) onBeat(beatCount);
    }, intervalMs);
  }

  public stopMetronome() {
    if (this.metronomeTimer !== null) {
      clearInterval(this.metronomeTimer);
      this.metronomeTimer = null;
    }
  }

  /**
   * Start Soothing Ambient Soundscape with Binaural Brainwave Entrainment
   * (Alpha 10Hz Focus / Theta 6Hz Relaxation / Delta 2Hz Deep Sleep)
   */
  public startAmbientSoundscape(type: 'ocean' | 'rain' | 'white_noise' | 'forest' | 'solfeggio' | 'raga_bhupali' | 'alpha_focus' | 'theta_calm' = 'solfeggio') {
    this.stopAmbientSoundscape();
    const ctx = this.initCtx();
    if (!ctx) return;

    try {
      const masterGain = ctx.createGain();
      masterGain.gain.setValueAtTime(0.001, ctx.currentTime);
      masterGain.gain.linearRampToValueAtTime(0.15, ctx.currentTime + 1.5);
      masterGain.connect(ctx.destination);

      const oscs: OscillatorNode[] = [];

      if (type === 'alpha_focus') {
        // Binaural Alpha Wave (200Hz Left, 210Hz Right -> 10Hz Alpha Focus Beat)
        const leftOsc = ctx.createOscillator();
        const rightOsc = ctx.createOscillator();
        leftOsc.frequency.setValueAtTime(200, ctx.currentTime);
        rightOsc.frequency.setValueAtTime(210, ctx.currentTime);
        leftOsc.connect(masterGain);
        rightOsc.connect(masterGain);
        leftOsc.start();
        rightOsc.start();
        oscs.push(leftOsc, rightOsc);
      } else if (type === 'theta_calm' || type === 'raga_bhupali') {
        // Indian Raga Bhupali Drone (Sa-Pa 240Hz / 360Hz + 6Hz Theta Entrainment)
        const frequencies = [240, 246, 360, 480];
        frequencies.forEach(f => {
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(f, ctx.currentTime);
          osc.connect(masterGain);
          osc.start();
          oscs.push(osc);
        });
      } else if (type === 'solfeggio' || type === 'ocean') {
        // 432 Hz Solfeggio Miracle Healing Frequency + 438 Hz Theta Binaural Beat
        const frequencies = [432, 438, 528, 639];
        frequencies.forEach(f => {
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(f, ctx.currentTime);
          osc.connect(masterGain);
          osc.start();
          oscs.push(osc);
        });
      } else {
        // Soft ambient warm sine chord (C Major9 Pentatonic)
        const chord = [261.63, 329.63, 392.00, 493.88, 523.25];
        chord.forEach(f => {
          const osc = ctx.createOscillator();
          osc.type = 'sine';
          osc.frequency.setValueAtTime(f, ctx.currentTime);
          osc.connect(masterGain);
          osc.start();
          oscs.push(osc);
        });
      }

      this.ambientNodes = { oscs, gain: masterGain };
      this.isAmbientPlaying = true;
    } catch (e) {
      console.warn('Ambient Soundscape error:', e);
    }
  }

  public stopAmbientSoundscape() {
    if (this.ambientNodes) {
      try {
        const { oscs, gain } = this.ambientNodes;
        if (this.ctx) {
          gain.gain.linearRampToValueAtTime(0.001, this.ctx.currentTime + 0.8);
          setTimeout(() => {
            oscs.forEach(o => o.stop());
          }, 850);
        }
      } catch (e) {
        // ignore
      }
      this.ambientNodes = null;
    }
    this.isAmbientPlaying = false;
  }

  public toggleGlobalBackgroundMusic(): boolean {
    if (this.isAmbientPlaying) {
      this.stopAmbientSoundscape();
      return false;
    } else {
      this.startAmbientSoundscape('raga_bhupali');
      return true;
    }
  }
}

export const audioEngine = new WebAudioEngine();
